import React from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ConditionNodeData {
  label?: string;
  // Support both flat format (condition: string) and nested format (condition: { expression: string })
  condition?: string | { expression?: string };
  trueLabel?: string;
  falseLabel?: string;
}

// Helper to extract condition expression from either format
function getConditionExpression(condition: string | { expression?: string } | undefined): string {
  if (!condition) return '';
  if (typeof condition === 'string') return condition;
  if (typeof condition === 'object' && condition.expression) return condition.expression;
  return '';
}

export default function ConditionNode({ 
  data, 
  selected 
}: { 
  data: ConditionNodeData; 
  selected?: boolean 
}) {
  return (
    <div className={cn(
      "min-w-[160px] rounded-lg pb-8", // Extra padding for handle labels
      "bg-card",
      "border-2 transition-all duration-fast",
      selected 
        ? "border-accent-yellow shadow-glow-focus" 
        : "border-accent-yellow/50 dark:border-accent-yellow/30 shadow-md hover:shadow-lg"
    )}>
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-accent-yellow !border-accent-yellow"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-accent-yellow/10 dark:bg-accent-yellow/20 flex items-center justify-center">
          <GitBranch className="w-3.5 h-3.5 text-accent-yellow" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Condition
          </div>
          <div className="text-xs text-muted-foreground/70 truncate">
            {getConditionExpression(data.condition) || 'Not configured'}
          </div>
        </div>
      </div>

      {/* Node Body - Empty for now, can add condition details */}
      <div className="p-3">
        <div className="text-xs text-muted-foreground">
          Evaluates condition and branches
        </div>
      </div>

      {/* Branch Handles */}
      <div className="relative">
        {/* True Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="true"
          className="!w-3 !h-3 !border-2 !bg-status-online !border-status-online"
          style={{ left: '30%', bottom: '-6px' }}
        />
        <div className="absolute text-xs font-medium text-status-online" style={{ left: '30%', bottom: '-24px', transform: 'translateX(-50%)' }}>
          {data.trueLabel || 'True'}
        </div>

        {/* False Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="false"
          className="!w-3 !h-3 !border-2 !bg-accent-red !border-accent-red"
          style={{ left: '70%', bottom: '-6px' }}
        />
        <div className="absolute text-xs font-medium text-accent-red" style={{ left: '70%', bottom: '-24px', transform: 'translateX(-50%)' }}>
          {data.falseLabel || 'False'}
        </div>
      </div>
    </div>
  );
}

